/**
 * Safe polygon hover handler for floating elements.
 *
 * Ported from @floating-ui/react's safePolygon (MIT License).
 * See: https://github.com/floating-ui/floating-ui/blob/master/packages/react/src/safePolygon.ts
 *
 * Generates a dynamic polygon "safe zone" between the reference element and
 * floating element so users can move the cursor from one to the other without
 * the floating element closing. This solves the classic "hover gap" problem
 * where a popover disappears when the mouse crosses the gap between trigger
 * and popover.
 */

type Point = [number, number];
type Polygon = Point[];
type Side = 'top' | 'bottom' | 'left' | 'right';

export interface SafePolygonContext {
  /** Cursor x when it left the reference element */
  x: number;
  /** Cursor y when it left the reference element */
  y: number;
  /** Placement side of the floating element relative to reference */
  placement: Side;
  /** The reference (trigger) element */
  referenceEl: HTMLElement;
  /** The floating (popover) element */
  floatingEl: HTMLElement;
  /** Called to close the floating element */
  onClose: () => void;
}

export interface SafePolygonOptions {
  /** Extra buffer around the polygon edges (px). Default: 0.5 */
  buffer?: number;
  /** Whether cursor must be moving toward the floating el to keep it open. Default: true */
  requireIntent?: boolean;
}

function isPointInPolygon(point: Point, polygon: Polygon): boolean {
  const [x, y] = point;
  let isInside = false;
  const length = polygon.length;
  for (let i = 0, j = length - 1; i < length; j = i++) {
    const [xi, yi] = polygon[i] || [0, 0];
    const [xj, yj] = polygon[j] || [0, 0];
    const intersect = yi >= y !== yj >= y && x <= ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) {
      isInside = !isInside;
    }
  }
  return isInside;
}

function isInsideRect(
  point: Point,
  rect: { x: number; y: number; width: number; height: number },
): boolean {
  return (
    point[0] >= rect.x &&
    point[0] <= rect.x + rect.width &&
    point[1] >= rect.y &&
    point[1] <= rect.y + rect.height
  );
}

function containsEl(parent: Element | null, child: Element | null): boolean {
  if (!parent || !child) return false;
  return parent.contains(child);
}

/**
 * Creates a mousemove handler that determines whether the cursor is within
 * a "safe" polygon between the reference and floating elements.
 */
export function createSafePolygonHandler(
  context: SafePolygonContext,
  options: SafePolygonOptions = {},
): (event: MouseEvent) => void {
  const { buffer = 0.5, requireIntent = true } = options;
  const { x, y, placement, referenceEl, floatingEl, onClose } = context;

  let hasLanded = false;
  let lastSpeedX: number | null = null;
  let lastSpeedY: number | null = null;
  let lastCursorTime = performance.now();
  let intentTimeout = -1;

  function getCursorSpeed(cx: number, cy: number): number | null {
    const now = performance.now();
    const elapsed = now - lastCursorTime;
    if (lastSpeedX === null || lastSpeedY === null || elapsed === 0) {
      lastSpeedX = cx;
      lastSpeedY = cy;
      lastCursorTime = now;
      return null;
    }
    const dx = cx - lastSpeedX;
    const dy = cy - lastSpeedY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const speed = distance / elapsed;
    lastSpeedX = cx;
    lastSpeedY = cy;
    lastCursorTime = now;
    return speed;
  }

  function computeRectPoly(side: Side, refRect: DOMRect, rect: DOMRect): Point[] {
    const isFloatingWider = rect.width > refRect.width;
    const isFloatingTaller = rect.height > refRect.height;
    const left = (isFloatingWider ? refRect : rect).left;
    const right = (isFloatingWider ? refRect : rect).right;
    const top = (isFloatingTaller ? refRect : rect).top;
    const bottom = (isFloatingTaller ? refRect : rect).bottom;

    switch (side) {
      case 'top':
        return [
          [left, refRect.top + 1],
          [left, rect.bottom - 1],
          [right, rect.bottom - 1],
          [right, refRect.top + 1],
        ];
      case 'bottom':
        return [
          [left, rect.top + 1],
          [left, refRect.bottom - 1],
          [right, refRect.bottom - 1],
          [right, rect.top + 1],
        ];
      case 'left':
        return [
          [rect.right - 1, bottom],
          [rect.right - 1, top],
          [refRect.left + 1, top],
          [refRect.left + 1, bottom],
        ];
      case 'right':
        return [
          [refRect.right - 1, bottom],
          [refRect.right - 1, top],
          [rect.left + 1, top],
          [rect.left + 1, bottom],
        ];
    }
  }

  function computeTriPoly(
    side: Side,
    exitX: number,
    exitY: number,
    rect: DOMRect,
    refRect: DOMRect,
  ): Point[] {
    const isFloatingWider = rect.width > refRect.width;
    const isFloatingTaller = rect.height > refRect.height;
    const cursorLeaveFromRight = exitX > rect.right - rect.width / 2;
    const cursorLeaveFromBottom = exitY > rect.bottom - rect.height / 2;

    switch (side) {
      case 'top': {
        const c1: Point = [
          isFloatingWider
            ? exitX + buffer / 2
            : cursorLeaveFromRight
              ? exitX + buffer * 4
              : exitX - buffer * 4,
          exitY + buffer + 1,
        ];
        const c2: Point = [
          isFloatingWider
            ? exitX - buffer / 2
            : cursorLeaveFromRight
              ? exitX + buffer * 4
              : exitX - buffer * 4,
          exitY + buffer + 1,
        ];
        return [
          c1,
          c2,
          [
            rect.left,
            cursorLeaveFromRight
              ? rect.bottom - buffer
              : isFloatingWider
                ? rect.bottom - buffer
                : rect.top,
          ],
          [
            rect.right,
            cursorLeaveFromRight
              ? isFloatingWider
                ? rect.bottom - buffer
                : rect.top
              : rect.bottom - buffer,
          ],
        ];
      }
      case 'bottom': {
        const c1: Point = [
          isFloatingWider
            ? exitX + buffer / 2
            : cursorLeaveFromRight
              ? exitX + buffer * 4
              : exitX - buffer * 4,
          exitY - buffer,
        ];
        const c2: Point = [
          isFloatingWider
            ? exitX - buffer / 2
            : cursorLeaveFromRight
              ? exitX + buffer * 4
              : exitX - buffer * 4,
          exitY - buffer,
        ];
        return [
          c1,
          c2,
          [
            rect.left,
            cursorLeaveFromRight
              ? rect.top + buffer
              : isFloatingWider
                ? rect.top + buffer
                : rect.bottom,
          ],
          [
            rect.right,
            cursorLeaveFromRight
              ? isFloatingWider
                ? rect.top + buffer
                : rect.bottom
              : rect.top + buffer,
          ],
        ];
      }
      case 'left': {
        const c1: Point = [
          exitX + buffer + 1,
          isFloatingTaller
            ? exitY + buffer / 2
            : cursorLeaveFromBottom
              ? exitY + buffer * 4
              : exitY - buffer * 4,
        ];
        const c2: Point = [
          exitX + buffer + 1,
          isFloatingTaller
            ? exitY - buffer / 2
            : cursorLeaveFromBottom
              ? exitY + buffer * 4
              : exitY - buffer * 4,
        ];
        return [
          [
            cursorLeaveFromBottom
              ? rect.right - buffer
              : isFloatingTaller
                ? rect.right - buffer
                : rect.left,
            rect.top,
          ],
          [
            cursorLeaveFromBottom
              ? isFloatingTaller
                ? rect.right - buffer
                : rect.left
              : rect.right - buffer,
            rect.bottom,
          ],
          c1,
          c2,
        ];
      }
      case 'right': {
        const c1: Point = [
          exitX - buffer,
          isFloatingTaller
            ? exitY + buffer / 2
            : cursorLeaveFromBottom
              ? exitY + buffer * 4
              : exitY - buffer * 4,
        ];
        const c2: Point = [
          exitX - buffer,
          isFloatingTaller
            ? exitY - buffer / 2
            : cursorLeaveFromBottom
              ? exitY + buffer * 4
              : exitY - buffer * 4,
        ];
        return [
          c1,
          c2,
          [
            cursorLeaveFromBottom
              ? rect.left + buffer
              : isFloatingTaller
                ? rect.left + buffer
                : rect.right,
            rect.top,
          ],
          [
            cursorLeaveFromBottom
              ? isFloatingTaller
                ? rect.left + buffer
                : rect.right
              : rect.left + buffer,
            rect.bottom,
          ],
        ];
      }
    }
  }

  return function onMouseMove(event: MouseEvent) {
    function close() {
      if (intentTimeout !== -1) {
        clearTimeout(intentTimeout);
        intentTimeout = -1;
      }
      onClose();
    }

    if (intentTimeout !== -1) {
      clearTimeout(intentTimeout);
      intentTimeout = -1;
    }

    const { clientX, clientY } = event;
    const clientPoint: Point = [clientX, clientY];
    const target = event.target as Element | null;
    const isLeave = event.type === 'mouseleave';
    const isOverFloatingEl = containsEl(floatingEl, target);
    const isOverReferenceEl = containsEl(referenceEl, target);
    const refRect = referenceEl.getBoundingClientRect();
    const rect = floatingEl.getBoundingClientRect();
    const side = placement;
    const isOverReferenceRect = isInsideRect(clientPoint, refRect);

    if (isOverFloatingEl) {
      hasLanded = true;
      if (!isLeave) return;
    }

    if (isOverReferenceEl) {
      hasLanded = false;
    }

    if (isOverReferenceEl && !isLeave) {
      hasLanded = true;
      return;
    }

    // Prevent stuck open-close loop when floating overlaps reference
    if (
      isLeave &&
      event.relatedTarget instanceof Element &&
      containsEl(floatingEl, event.relatedTarget)
    ) {
      return;
    }

    // If pointer is leaving from opposite side, close immediately
    if (
      (side === 'top' && y >= refRect.bottom - 1) ||
      (side === 'bottom' && y <= refRect.top + 1) ||
      (side === 'left' && x >= refRect.right - 1) ||
      (side === 'right' && x <= refRect.left + 1)
    ) {
      return close();
    }

    // Rectangular trough between the two elements — always safe
    if (isPointInPolygon([clientX, clientY], computeRectPoly(side, refRect, rect))) {
      return;
    }

    // If user has already been inside floating and moves out of reference rect, close
    if (hasLanded && !isOverReferenceRect) {
      return close();
    }

    // Check intent (cursor speed)
    if (!isLeave && requireIntent) {
      const cursorSpeed = getCursorSpeed(event.clientX, event.clientY);
      const cursorSpeedThreshold = 0.1;
      if (cursorSpeed !== null && cursorSpeed < cursorSpeedThreshold) {
        return close();
      }
    }

    if (!isPointInPolygon([clientX, clientY], computeTriPoly(side, x, y, rect, refRect))) {
      close();
    } else if (!hasLanded && requireIntent) {
      intentTimeout = window.setTimeout(close, 40);
    }
  };
}
